// PR-1d — dual-identity ball-holder display resolver.
//
// PR-1a established the schema: `league_matches.ball_holder_user_id` (FK
// to `profiles`) + `league_matches.ball_holder_customer_id` (FK to
// `customers`) gated by a mutex CHECK (`league_matches_ball_holder_mutex`,
// num_nonnulls(...) <= 1). PR-1c flipped the write fence so the picker's
// kind discriminator routes to the matching column.
//
// PR-1d makes display surfaces aware of the dual-FK pattern. Callers
// FK-embed both joined tables and pass the row to
// `resolveBallHolderFromEmbed`. The returned discriminated result is
// safe to render directly without re-checking nulls.
//
// Fallback chain (matches `resolveSlotDisplay` in `teamHelpers.ts` for
// stylistic consistency):
//   - `kind: 'user'`     → `ball_holder_user.full_name?.trim()` || `"Spelare"`
//   - `kind: 'customer'` → `ball_holder_customer.full_name?.trim()` || `"Spelare"`
//   - both NULL          → `{ kind: 'none' }`
//
// Mutex invariant: the CHECK guarantees the both-set case can never come
// from PROD. If the caller manually constructs an invalid in-memory
// embed (e.g. a unit-test fixture mistake), the resolver silently prefers
// `kind: 'user'` to match PR-1c picker's discriminator-priority ordering.
// Pure return — no console output (matches `ballHolder.ts` / `teamHelpers.ts`
// style).

/** Display fallback when full_name is null/blank or the embed is missing. */
const UNKNOWN_DISPLAY = "Spelare";

/**
 * Result of resolving a league_matches row's dual-FK ball-holder. The
 * discriminated `kind` field tells the caller exactly which DB path
 * carried the holder. `displayName` is always non-empty (falls back to
 * `"Spelare"` when full_name is null/blank or the embed is missing).
 */
export type ResolvedBallHolder =
  | { kind: "user"; userId: string; displayName: string }
  | { kind: "customer"; customerId: string; displayName: string }
  | { kind: "none" };

/**
 * Shape expected by `resolveBallHolderFromEmbed`. Callers must FK-embed
 * the joined profile + customer when fetching the league_matches row, e.g.:
 *
 *   ball_holder_user_id, ball_holder_customer_id,
 *   ball_holder_user:profiles!league_matches_ball_holder_user_id_fkey (full_name),
 *   ball_holder_customer:customers!league_matches_ball_holder_customer_id_fkey (full_name)
 *
 * The `?` on the embed fields tolerates callers who don't need the display
 * name (e.g. the reminder edge fn's skip-decision only needs the FK column,
 * not the joined full_name).
 */
export interface BallHolderEmbed {
  ball_holder_user_id: string | null;
  ball_holder_customer_id: string | null;
  ball_holder_user?: { full_name: string | null } | null;
  ball_holder_customer?: { full_name: string | null } | null;
}

/**
 * Resolve a league_matches row's dual-FK ball-holder into a discriminated
 * display result. Synchronous + pure — works directly with FK-embedded
 * data from a Supabase `.select()`.
 *
 * Returns `{ kind: 'none' }` when both FK columns are null. Returns
 * `{ kind: 'user', userId, displayName }` or
 * `{ kind: 'customer', customerId, displayName }` for the populated case.
 * `displayName` is always non-empty (`"Spelare"` fallback when full_name
 * is null/blank or the joined row is missing).
 */
export function resolveBallHolderFromEmbed(row: BallHolderEmbed): ResolvedBallHolder {
  // Defensive: the DB-level mutex CHECK (PR-1a) guarantees at most one of
  // these is non-null per row. If a caller constructs an invalid in-memory
  // embed (e.g. unit-test fixture), prefer the user kind silently — matches
  // PR-1c picker's kind: 'user' discriminator-priority ordering.
  if (row.ball_holder_user_id) {
    const displayName =
      row.ball_holder_user?.full_name?.trim() || UNKNOWN_DISPLAY;
    return { kind: "user", userId: row.ball_holder_user_id, displayName };
  }
  if (row.ball_holder_customer_id) {
    const displayName =
      row.ball_holder_customer?.full_name?.trim() || UNKNOWN_DISPLAY;
    return {
      kind: "customer",
      customerId: row.ball_holder_customer_id,
      displayName,
    };
  }
  return { kind: "none" };
}
